import { useCallback, useEffect, useState } from 'react';
import type { ResumenMensual, TarifaEquipo, UsuarioSesion } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, localeDe, t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { FinanzasRegistro } from './FinanzasRegistro.js';
import { Icono } from '../componentes/Iconos.js';
import { TarifasEquipoTabla } from '../componentes/TarifasEquipo.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onVolver: () => void;
}

/*
  LOS MESES NO SE ESCRIBEN A MANO EN TRES IDIOMAS.

  Estaban en castellano dentro del fichero. `Intl` ya sabe como se llaman los
  meses en cada sitio —y como se escriben, que en aleman llevan mayuscula y en
  ingles tambien—, asi que basta con pedirselos. Anadir un idioma no obliga a
  volver aqui.
*/
function mesesDe(idioma: Idioma): string[] {
  const formato = new Intl.DateTimeFormat(localeDe(idioma), { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) =>
    formato.format(new Date(Date.UTC(2020, i, 1))),
  ).map((nombre) => nombre.charAt(0).toUpperCase() + nombre.slice(1));
}

/**
 * Finanzas — resumen mensual.
 *
 * Convierte las horas y los gastos del mes en las líneas que se facturan. El
 * cálculo (cuota fija, horas, margen, IVA según el modo de cada propiedad) lo
 * hace el servidor; aquí sólo se presenta, con el dinero formateado en el borde.
 */
export function Finanzas({ usuario, idioma, onVolver }: Props) {
  const ahora = new Date();
  const [anio, setAnio] = useState(ahora.getUTCFullYear());
  const [mes, setMes] = useState(ahora.getUTCMonth() + 1);
  const [resumen, setResumen] = useState<ResumenMensual | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [tarifas, setTarifas] = useState<TarifaEquipo[]>([]);
  const esDireccion = usuario.rol === 'DIRECTOR';

  const cargarTarifas = useCallback(() => {
    if (!esDireccion) return;
    api
      .finTarifasEquipo()
      .then(({ tarifas: lista }) => setTarifas(lista))
      .catch(() => setTarifas([]));
  }, [esDireccion]);
  useEffect(cargarTarifas, [cargarTarifas]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api
      .finResumen(anio, mes)
      .then(({ resumen: r }) => setResumen(r))
      .catch(() => setError(t(idioma, 'fin.noSeCargo')))
      .finally(() => setCargando(false));
  }, [anio, mes, idioma]);

  useEffect(cargar, [cargar]);

  const importe = (cents: number) => formatearImporteDesdeCents(cents, idioma);
  const meses = mesesDe(idioma);

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'fin.titulo')}</h1>
          <p>{t(idioma, 'fin.sub')}</p>
        </div>
        <div className="acciones">
          <button type="button" onClick={() => setRegistrando((v) => !v)}>
            {t(idioma, registrando ? 'fin.ocultarRegistro' : 'fin.registrar')}
          </button>
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {registrando && (
        <FinanzasRegistro
          usuario={usuario}
          idioma={idioma}
          onRegistrado={() => {
            cargar();
          }}
        />
      )}

      {esDireccion && tarifas.length > 0 && (
        <TarifasEquipoTabla
          idioma={idioma}
          propiedadId={null}
          tarifas={tarifas}
          onCambio={() => {
            cargarTarifas();
            cargar();
          }}
        />
      )}

      <div className="fin-periodo">
        <div className="campo">
          <label htmlFor="fin-mes">{t(idioma, 'fin.mes')}</label>
          <select id="fin-mes" value={mes} onChange={(ev) => setMes(Number(ev.target.value))}>
            {meses.map((nombre, i) => (
              <option key={nombre} value={i + 1}>
                {nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="fin-anio">{t(idioma, 'fin.anio')}</label>
          <select id="fin-anio" value={anio} onChange={(ev) => setAnio(Number(ev.target.value))}>
            {[anio - 1, anio, anio + 1].map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {cargando && <p>{t(idioma, 'app.cargando')}</p>}

      {resumen && !cargando && (
        <>
          <div className="fin-totales">
            <div className="fin-total">
              <span>{t(idioma, 'fin.base')}</span>
              <strong>{importe(resumen.totales.baseCents)}</strong>
            </div>
            <div className="fin-total">
              <span>{t(idioma, 'fin.iva')}</span>
              <strong>{importe(resumen.totales.ivaCents)}</strong>
            </div>
            <div className="fin-total fin-total-destacado">
              <span>{t(idioma, 'fin.total')}</span>
              <strong>{importe(resumen.totales.totalCents)}</strong>
            </div>
          </div>

          {resumen.lineas.length === 0 ? (
            <div className="tarjeta">
              <p>{t(idioma, 'fin.vacio', { mes: meses[mes - 1] ?? '', anio })}</p>
            </div>
          ) : (
            <div className="tarjeta tabla-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t(idioma, 'fin.colTipo')}</th>
                    <th scope="col">{t(idioma, 'fin.colConcepto')}</th>
                    <th scope="col">{t(idioma, 'fin.colPropiedad')}</th>
                    <th scope="col">{t(idioma, 'fin.colPersona')}</th>
                    <th scope="col">{t(idioma, 'fin.colBase')}</th>
                    <th scope="col">{t(idioma, 'fin.iva')}</th>
                    <th scope="col">{t(idioma, 'fin.colTotal')}</th>
                    <th scope="col">{t(idioma, 'fin.colFacturacion')}</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.lineas.map((l, i) => (
                    <tr key={`${l.tipo}-${l.propertyId ?? ''}-${l.userId ?? ''}-${i}`}>
                      <td>{t(idioma, `fin.tipo${l.tipo}`)}</td>
                      <td>
                        {l.concepto}
                        {l.horas != null && (
                          <span className="fin-horas"> · {l.horas} h</span>
                        )}
                      </td>
                      <td>{l.propertyNombre ?? '—'}</td>
                      <td>{l.userNombre ?? '—'}</td>
                      <td className="fin-num">{importe(l.baseCents)}</td>
                      <td className="fin-num">{l.ivaCents > 0 ? importe(l.ivaCents) : '—'}</td>
                      <td className="fin-num">{importe(l.totalCents)}</td>
                      <td>
                        <span className="pastilla neutra">
                          {t(idioma, `fin.modo${l.modo}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </main>
  );
}
